import type {
  ApplyActivationCodeInput,
  ApplyRewardCodeInput,
  AuthActionResult,
  EmailOtpActionResult,
  AuthState,
  HeartbeatBootstrap,
  IssueUserProfileTransferInput,
  InviteRecordsResult,
  LicenseSummary,
  MarkFeedbackConversationReadInput,
  LoginInput,
  PasswordResetInput,
  ResetSecondaryPasswordByEmailInput,
  RedeemInviteCodeInput,
  RegisterInput,
  SendEmailOtpInput,
  ConsumeScanTicketInput,
  CreateScanTicketInput,
  ScanTicket,
  SecondaryPasswordActionResult,
  SetSecondaryPasswordInput,
  SubmitFeedbackInput,
  SyncCloudProfilesInput,
  UserProfile,
  VerifySecondaryPasswordInput,
} from '../../shared/types';

type CodexWorkspaceApi = typeof window.codexWorkspace;
type ScanApi = Pick<
  CodexWorkspaceApi,
  'createScanTicket' | 'getScanTicket' | 'getScanCodeUrl' | 'consumeScanTicket'
>;

const api = () => window.codexWorkspace;
const getScanApi = (): ScanApi => {
  const bridge = api() as Partial<ScanApi>;
  if (
    typeof bridge.createScanTicket !== 'function' ||
    typeof bridge.getScanTicket !== 'function' ||
    typeof bridge.getScanCodeUrl !== 'function' ||
    typeof bridge.consumeScanTicket !== 'function'
  ) {
    throw new Error('当前客户端缺少完整的扫码登录接口，请更新本地安装包后再试。');
  }
  return bridge as ScanApi;
};

export const authClient = {
  getState: (): Promise<AuthState> => api().getAuthState(),
  login: (input: LoginInput): Promise<AuthActionResult> => api().login(input),
  register: (input: RegisterInput): Promise<AuthActionResult> => api().register(input),
  sendEmailOtp: (input: SendEmailOtpInput): Promise<EmailOtpActionResult> => api().sendEmailOtp(input),
  requestPasswordReset: (input: PasswordResetInput): Promise<AuthActionResult> =>
    api().requestPasswordReset(input),
  createScanTicket: (input: CreateScanTicketInput = {}): Promise<ScanTicket> =>
    getScanApi().createScanTicket(input),
  getScanTicket: (ticketId: string): Promise<ScanTicket> => getScanApi().getScanTicket(ticketId),
  getScanCodeUrl: (ticketId: string): Promise<string> => getScanApi().getScanCodeUrl(ticketId),
  consumeScanTicket: (input: ConsumeScanTicketInput): Promise<AuthActionResult> =>
    getScanApi().consumeScanTicket(input),
  getAccountProfile: (): Promise<UserProfile | null> => api().getAccountProfile(),
  getLicenseSummary: (): Promise<LicenseSummary> => api().getLicenseSummary(),
  applyActivationCode: (input: ApplyActivationCodeInput): Promise<AuthActionResult> =>
    api().applyActivationCode(input),
  requestInviteCode: (): Promise<AuthActionResult> => api().requestInviteCode(),
  redeemInviteCode: (input: RedeemInviteCodeInput): Promise<AuthActionResult> =>
    api().redeemInviteCode(input),
  claimInviteReward: (): Promise<AuthActionResult> => api().claimInviteReward(),
  applyRewardCode: (input: ApplyRewardCodeInput): Promise<AuthActionResult> =>
    api().applyRewardCode(input),
  listInviteRecords: (): Promise<InviteRecordsResult> => api().listInviteRecords(),
  submitFeedback: (input: SubmitFeedbackInput): Promise<AuthActionResult> =>
    api().submitFeedback(input),
  markFeedbackConversationRead: (
    input: MarkFeedbackConversationReadInput,
  ): Promise<AuthActionResult> => api().markFeedbackConversationRead(input),
  issueUserProfileTransfer: (input: IssueUserProfileTransferInput): Promise<AuthActionResult> =>
    api().issueUserProfileTransfer(input),
  setSecondaryPassword: (input: SetSecondaryPasswordInput): Promise<SecondaryPasswordActionResult> =>
    api().setSecondaryPassword(input),
  resetSecondaryPasswordByEmail: (
    input: ResetSecondaryPasswordByEmailInput,
  ): Promise<SecondaryPasswordActionResult> => api().resetSecondaryPasswordByEmail(input),
  verifySecondaryPassword: (
    input: VerifySecondaryPasswordInput,
  ): Promise<SecondaryPasswordActionResult> => api().verifySecondaryPassword(input),
  syncCloudProfiles: (input: SyncCloudProfilesInput): Promise<AuthActionResult> =>
    api().syncCloudProfiles(input),
  heartbeat: (): Promise<AuthActionResult> => api().heartbeat(),
  getHeartbeatBootstrap: (): Promise<HeartbeatBootstrap> => api().getHeartbeatBootstrap(),
  onStateChange: (listener: (state: AuthState) => void): (() => void) => api().onAuthStateChange(listener),
};
